import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Collapse, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import filterIcon from "assets/images/filterIcon.svg";
import mappedIcon from "assets/images/mappedicon.svg";
import { useParams } from "react-router-dom";
import type { StoreAuditSummaryRangeDayModel, StoreReconciliationAuditShiftModel } from "models";
import {
  useAuditDashboardStoreReconciliationAuditsByDate,
  useAuditDashboardStoreScratcherReconciliationAuditsRange,
} from "../../hooks";
import { auditDashboardTheme, auditToneStyles, AuditTone } from "../../theme";
import {
  formatCurrency,
  formatShiftRangeLabel,
  getShiftOperatorLabel,
  buildShiftDisplayLabelRows,
  hasScratcherMismatch,
} from "../../utils";
import ShiftScratcherDetails from "./ShiftScratcherDetails";

interface AuditShiftBreakdownSectionProps {
  day: StoreAuditSummaryRangeDayModel | null;
  startDate?: string | null;
  endDate?: string | null;
  disableShiftExpand?: boolean;
  hasPosType?: boolean;
}

const formatCount = (value: number) => Number(value ?? 0).toLocaleString();

const getShiftTone = (shift: StoreReconciliationAuditShiftModel): Exclude<AuditTone, "progress"> => {
  if (shift.ignorePosData) {
    return "default";
  }

  const qtyVariance = shift.scratcherQtyVariance;
  const salesVariance = shift.scratcherSalesVariance;

  if (qtyVariance == null && salesVariance == null) {
    return "default";
  }

  if ((qtyVariance ?? 0) < 0 || (salesVariance ?? 0) < 0) {
    return "negative";
  }

  if ((qtyVariance ?? 0) > 0 || (salesVariance ?? 0) > 0) {
    return "positive";
  }

  return "matched";
};

const renderVariance = (value: number | null | undefined, currency = false) => {
  if (value == null) {
    return (
      <Box
        component="span"
        sx={{
          color: auditDashboardTheme.colors.textMutedStrong,
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.3,
          whiteSpace: "nowrap",
        }}
      >
        -
      </Box>
    );
  }

  if (value === 0) {
    return (
      <Box
        component="img"
        src={mappedIcon}
        alt="Matched"
        aria-label="No variance"
        sx={{
          display: "inline-block",
          width: 17,
          height: 17,
          verticalAlign: "middle",
        }}
      />
    );
  }

  const prefix = value > 0 ? "+" : "-";
  const displayValue = currency ? `${prefix}${formatCurrency(Math.abs(value))}` : `${prefix}${formatCount(Math.abs(value))}`;

  return (
    <Box
      component="span"
      sx={{
        color: value > 0 ? auditDashboardTheme.colors.textPositive : auditDashboardTheme.colors.textNegative,
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
      }}
    >
      {displayValue}
    </Box>
  );
};

const formatMovementValue = (value: number | null, mode: "plain" | "plus" | "minus") => {
  if (value == null) return "";

  const resolvedValue = Number(value ?? 0);
  if (!resolvedValue) return "0";

  const prefix = mode === "plus" && resolvedValue > 0 ? "+" : mode === "minus" && resolvedValue > 0 ? "-" : "";
  return `${prefix}${formatCount(resolvedValue)}`;
};

const getLocationChipSx = (locationType: string | null | undefined) => {
  const isVending = locationType?.toLowerCase() === "vending";

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 18,
    px: "8px",
    border: isVending ? "1px solid rgba(223, 148, 0, 0.38)" : "1px solid rgba(44, 108, 160, 0.28)",
    borderRadius: 999,
    bgcolor: isVending ? auditDashboardTheme.colors.amberSoft : "rgba(44, 108, 160, 0.08)",
    color: isVending ? auditDashboardTheme.colors.amber : auditDashboardTheme.colors.blue,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: "nowrap",
    textTransform: "uppercase",
  };
};

type ShiftSourceOption = {
  key: string;
  label: string;
  type: string;
};

const ALL_SHIFT_SOURCE_KEY = "all";

const getShiftExpansionKey = (shift: StoreReconciliationAuditShiftModel) =>
  shift.auditId || `shift-${[
    shift.reportDate,
    shift.reportDatetime,
    shift.locationKey,
    shift.locationName,
    shift.shiftStartDate,
    shift.shiftStartTime,
    shift.shiftEndDate,
    shift.shiftEndTime,
    shift.userName,
    shift.operatorName,
  ].filter(Boolean).join("::") || "unknown"}`;

const formatShiftSourceType = (locationType: string | null | undefined) => {
  const normalizedType = String(locationType || "").trim().toLowerCase();

  if (normalizedType === "vending") {
    return "Vending";
  }

  if (normalizedType === "counter") {
    return "Counter";
  }

  return "";
};

const getShiftSourceKey = (shift: StoreReconciliationAuditShiftModel) => {
  return String(shift.locationType || shift.locationName || shift.locationKey || "").trim().toLowerCase();
};

const getShiftSourceLabel = (shift: StoreReconciliationAuditShiftModel) => {
  return formatShiftSourceType(shift.locationType) || String(shift.locationName || shift.locationKey || shift.locationType || "").trim();
};

function ShiftSummaryTable({
  shift,
  hasPosType,
}: {
  shift: StoreReconciliationAuditShiftModel;
  hasPosType: boolean;
}) {
  const canUsePosData = hasPosType && !shift.ignorePosData;
  const movementMetrics = [
    {
      label: "Opening",
      ticketCount: Number(shift.openingCount ?? 0),
      packCount: Number(shift.openingPackCount ?? 0),
      mode: "plain" as const,
    },
    {
      label: "Activated",
      ticketCount: Number(shift.newlyActivatedCount ?? 0),
      packCount: Number(shift.newlyActivatedPackCount ?? 0),
      mode: "plus" as const,
    },
    {
      label: "Moved In",
      ticketCount: Number(shift.movedFromBackstockTicketCount ?? 0),
      packCount: Number(shift.movedFromBackstockCount ?? 0),
      mode: "plus" as const,
    },
    {
      label: "Moved Out",
      ticketCount: Number(shift.movedToBackstockTicketCount ?? 0),
      packCount: Number(shift.movedToBackstockCount ?? 0),
      mode: "minus" as const,
    },
    {
      label: "Sold Out",
      ticketCount: null,
      packCount: Number(shift.counterSoldOutPackCount ?? 0),
      mode: "minus" as const,
    },
    {
      label: "Closing",
      ticketCount: Number(shift.closingCount ?? 0),
      packCount: Number(shift.closingPackCount ?? 0),
      mode: "plain" as const,
    },
  ].filter(
    (item) => item.mode === "plain" || (item.ticketCount ?? 0) !== 0 || item.packCount !== 0
  );

  return (
    <Box
      sx={{
        width: { xs: "calc(100% - 24px)", sm: "min(760px, calc(100% - 80px))" },
        mx: "auto",
        pt: 0,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(96px, 0.72fr) minmax(108px, 0.78fr) minmax(84px, 0.62fr)",
          columnGap: 1,
          alignItems: "center",
          borderBottom: `1px solid ${auditDashboardTheme.colors.line}`,
          bgcolor: "rgba(255, 255, 255, 0.5)",
          color: auditDashboardTheme.colors.text,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: "7px",
          }}
        />
        {["Sales Expected", "Sales Reported", "Variance"].map((label) => (
          <Typography
            key={label}
            component="div"
            sx={{
              px: 1.5,
              py: "7px",
              color: auditDashboardTheme.colors.text,
              fontSize: 12,
              fontWeight: 800,
              textAlign: "right",
              lineHeight: 1.15,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      {[
        {
          id: "qty",
          label: "Scratcher Qty",
          expected: formatCount(shift.scratcherExpectedQty),
          reported: canUsePosData ? formatCount(shift.scratcherReportedQty) : "-",
          variance: canUsePosData ? renderVariance(shift.scratcherQtyVariance) : renderVariance(null),
        },
        {
          id: "sales",
          label: "Scratcher $",
          expected: formatCurrency(shift.scratcherExpectedSales),
          reported: canUsePosData ? formatCurrency(shift.scratcherReportedSales) : "-",
          variance: canUsePosData ? renderVariance(shift.scratcherSalesVariance, true) : renderVariance(null),
        },
      ].map((row, index) => (
        <Box
          key={row.id}
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(96px, 0.72fr) minmax(108px, 0.78fr) minmax(84px, 0.62fr)",
            columnGap: 1,
            alignItems: "center",
          }}
        >
          <Typography
            component="div"
            sx={{
              px: 1.5,
              py: "4px",
              color: auditDashboardTheme.colors.text,
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1.3,
              textAlign: "left",
            }}
          >
            {row.label}
          </Typography>
          <Typography
            component="div"
            sx={{
              px: 1.5,
              py: "4px",
              color: auditDashboardTheme.colors.text,
              fontSize: 13,
              fontWeight: 400,
              lineHeight: 1.3,
              textAlign: "right",
              whiteSpace: "nowrap",
            }}
          >
            {row.expected}
          </Typography>
          <Typography
            component="div"
            sx={{
              px: 1.5,
              py: "4px",
              color: auditDashboardTheme.colors.text,
              fontSize: 13,
              fontWeight: 400,
              lineHeight: 1.3,
              textAlign: "right",
              whiteSpace: "nowrap",
            }}
          >
            {row.reported}
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: "4px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {row.variance}
          </Box>
        </Box>
      ))}

      {movementMetrics.length ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "stretch",
            flexWrap: { xs: "wrap", md: "nowrap" },
            gap: { xs: "18px", md: 0 },
            mt: "8px",
            mx: 0,
            px: 0,
            py: "8px",
            color: auditDashboardTheme.colors.textMutedStrong,
            fontSize: 13,
            borderTop: "1px solid #dee2ee",
          }}
        >
          {movementMetrics.map((item, index) => {
            const ticketText = formatMovementValue(item.ticketCount, item.mode);
            const packText = formatMovementValue(item.packCount, item.mode);
            const hasBothValues = Boolean(ticketText && packText);

            return (
              <Box
                key={item.label}
                sx={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  minWidth: { xs: 118, md: 132 },
                  whiteSpace: "nowrap",
                  ...(index > 0
                    ? {
                        ml: { xs: 0, md: 1.5 },
                        pl: { xs: 0, md: 1.5 },
                        borderLeft: { xs: "none", md: `1px solid ${auditDashboardTheme.colors.line}` },
                      }
                    : {}),
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.1,
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  {item.label}
                </Box>
                <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "12px", mt: "10px" }}>
                  {ticketText ? (
                    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                      <Box component="span" sx={{ color: auditDashboardTheme.colors.text, fontSize: 12, fontWeight: 500, lineHeight: 1.1 }}>
                        {ticketText}
                      </Box>
                      <Box component="span" sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.1 }}>
                        Tickets
                      </Box>
                    </Box>
                  ) : null}
                  {hasBothValues ? <Box sx={{ width: 1, height: 28, bgcolor: auditDashboardTheme.colors.line }} /> : null}
                  {packText ? (
                    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                      <Box component="span" sx={{ color: auditDashboardTheme.colors.text, fontSize: 12, fontWeight: 500, lineHeight: 1.1 }}>
                        {packText}
                      </Box>
                      <Box component="span" sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.1 }}>
                        Packs
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
}

export default function AuditShiftBreakdownSection({
  day,
  startDate,
  endDate,
  disableShiftExpand = false,
  hasPosType = true,
}: AuditShiftBreakdownSectionProps) {
  const { id: storeId } = useParams<{ id: string }>();
  const [expandedShiftKeys, setExpandedShiftKeys] = useState<Set<string>>(() => new Set());
  const [selectedSourceKey, setSelectedSourceKey] = useState("");
  const isRangeSummary = Boolean(startDate && endDate);

  const auditsQuery = useAuditDashboardStoreReconciliationAuditsByDate(
    storeId,
    day?.reportDate,
    Boolean(storeId && day?.reportDate && !isRangeSummary)
  );
  const rangeAuditsQuery = useAuditDashboardStoreScratcherReconciliationAuditsRange(
    storeId,
    startDate,
    endDate,
    Boolean(storeId && startDate && endDate && isRangeSummary)
  );
  const shifts = useMemo(() => {
    return isRangeSummary ? rangeAuditsQuery.data?.shifts ?? [] : auditsQuery.data?.shifts ?? [];
  }, [auditsQuery.data?.shifts, isRangeSummary, rangeAuditsQuery.data?.shifts]);
  const isLoading = isRangeSummary
    ? rangeAuditsQuery.isLoading || (rangeAuditsQuery.isFetching && !rangeAuditsQuery.data)
    : auditsQuery.isLoading || (auditsQuery.isFetching && !auditsQuery.data);
  const isError = isRangeSummary ? rangeAuditsQuery.isError : auditsQuery.isError;
  const sourceOptions = useMemo(() => {
    if (!isRangeSummary) {
      return [];
    }

    const options = new Map<string, ShiftSourceOption>();
    shifts.forEach((shift) => {
      const key = getShiftSourceKey(shift);
      const label = getShiftSourceLabel(shift);
      const type = String(shift.locationType || "").trim();

      if (!key || !label || options.has(key)) {
        return;
      }

      options.set(key, { key, label, type });
    });

    return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [isRangeSummary, shifts]);
  const defaultSourceKey = useMemo(() => {
    return sourceOptions.length ? ALL_SHIFT_SOURCE_KEY : "";
  }, [sourceOptions]);
  const showSourceFilter = isRangeSummary && sourceOptions.length > 1;
  const effectiveSourceKey = isRangeSummary ? selectedSourceKey || defaultSourceKey : "";
  const hasActiveSourceFilter = showSourceFilter && effectiveSourceKey !== ALL_SHIFT_SOURCE_KEY;
  const visibleShifts = useMemo(() => {
    if (!showSourceFilter || !effectiveSourceKey || effectiveSourceKey === ALL_SHIFT_SOURCE_KEY) {
      return shifts;
    }

    return shifts.filter((shift) => getShiftSourceKey(shift) === effectiveSourceKey);
  }, [effectiveSourceKey, shifts, showSourceFilter]);
  const visibleShiftRows = useMemo(
    () => buildShiftDisplayLabelRows(visibleShifts, { resetByReportDate: true }),
    [visibleShifts]
  );

  const toggleShiftExpanded = useCallback((shiftKey: string) => {
    setExpandedShiftKeys((current) => {
      const next = new Set(current);
      if (next.has(shiftKey)) {
        next.delete(shiftKey);
      } else {
        next.add(shiftKey);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setExpandedShiftKeys(new Set());
  }, [day?.reportDate, endDate, startDate]);

  useEffect(() => {
    if (!isRangeSummary || !sourceOptions.length) {
      if (selectedSourceKey) {
        setSelectedSourceKey("");
      }
      return;
    }

    const selectedExists = selectedSourceKey
      ? selectedSourceKey === ALL_SHIFT_SOURCE_KEY || sourceOptions.some((option) => option.key === selectedSourceKey)
      : false;

    if (!selectedExists && defaultSourceKey) {
      setSelectedSourceKey(defaultSourceKey);
    }
  }, [defaultSourceKey, isRangeSummary, selectedSourceKey, sourceOptions]);

  if (!day) {
    return null;
  }

  return (
    <Box
      sx={{
        overflow: "visible",
      }}
    >
      <Box sx={{ p: 0 }}>
        {isLoading ? (
          <Box sx={{ minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Box sx={{ minHeight: 96, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography component="div" sx={{ fontSize: 12, color: "text.secondary" }}>
              Unable to load shift breakdown.
            </Typography>
          </Box>
        ) : shifts.length === 0 ? (
          <Box sx={{ minHeight: 96, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#dfe0e4" }}>
            <Typography component="div" sx={{ fontSize: 14, color: "text.secondary" }}>
              No Audits Found
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                mb: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
                minWidth: 0,
                flexWrap: "wrap",
              }}
            >
              <Typography
                component="div"
                sx={{
                  color: auditDashboardTheme.colors.text,
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {`${visibleShifts.length} ${visibleShifts.length === 1 ? "Shift" : "Shifts"} Audited`}
              </Typography>

              {showSourceFilter ? (
                <Box
                  sx={{
                    ml: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "18px",
                    minWidth: 0,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      position: "relative",
                      width: 22,
                      height: 22,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="span"
                      aria-hidden
                      sx={{
                        width: 18,
                        height: 18,
                        display: "inline-block",
                        bgcolor: hasActiveSourceFilter ? auditDashboardTheme.colors.blue : auditDashboardTheme.colors.textMutedStrong,
                        mask: `url(${filterIcon}) center / contain no-repeat`,
                        WebkitMask: `url(${filterIcon}) center / contain no-repeat`,
                      }}
                    />
                    {hasActiveSourceFilter ? (
                      <Tooltip title="Clear location filter">
                        <IconButton
                          size="small"
                          aria-label="Clear location filter"
                          onClick={() => setSelectedSourceKey(ALL_SHIFT_SOURCE_KEY)}
                          sx={{
                            position: "absolute",
                            top: -5,
                            right: -6,
                            width: 16,
                            height: 16,
                            p: 0,
                            border: "1px solid",
                            borderColor: auditDashboardTheme.colors.surface,
                            bgcolor: "#ff5b8a",
                            color: auditDashboardTheme.colors.surface,
                            boxShadow: "0 1px 3px rgba(34, 43, 69, 0.18)",
                            "&:hover": {
                              bgcolor: "#e94d7c",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 11 }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                  <Tabs
                    value={effectiveSourceKey}
                    onChange={(_, value: string) => setSelectedSourceKey(value)}
                    sx={{
                      minHeight: 0,
                      "& .MuiTabs-flexContainer": {
                        gap: "20px",
                        alignItems: "center",
                        flexWrap: "wrap",
                      },
                      "& .MuiTabs-indicator": { display: "none" },
                      "& .MuiTab-root": {
                        minHeight: 0,
                        minWidth: 0,
                        p: 0,
                        color: auditDashboardTheme.colors.textMutedStrong,
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        textTransform: "none",
                        borderRadius: 0,
                        border: 0,
                      },
                      "& .Mui-selected": {
                        color: `${auditDashboardTheme.colors.blue} !important`,
                        bgcolor: "transparent",
                        fontSize: 14,
                      },
                    }}
                  >
                    <Tab label="All" value={ALL_SHIFT_SOURCE_KEY} />
                    {sourceOptions.map((option) => (
                      <Tab key={option.key} label={option.label} value={option.key} />
                    ))}
                  </Tabs>
                </Box>
              ) : null}
            </Box>

            {visibleShifts.length === 0 ? (
              <Box sx={{ minHeight: 96, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#dfe0e4" }}>
                <Typography component="div" sx={{ fontSize: 12, color: "text.secondary" }}>
                  No shift data found.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px" }}>
                {visibleShiftRows.map(({ shift, displayLabel, locationLabel }) => {
                  const shiftKey = getShiftExpansionKey(shift);
                  const shiftReportDate = shift.reportDate || day.reportDate;
                  const isShiftExpanded = expandedShiftKeys.has(shiftKey);
                  const shiftReportPanelId = `shift-report-panel-${shiftKey.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
                  const shiftTone = getShiftTone(shift);
                  const isShiftMismatched = hasPosType
                    && !shift.ignorePosData
                    && hasScratcherMismatch(shift.scratcherQtyVariance, shift.scratcherSalesVariance);

              return (
                <Box
                  key={shiftKey}
                  sx={{
                    border: "1px solid",
                    borderColor: auditToneStyles[shiftTone].border,
                    borderRadius: "8px",
                    boxShadow: "none",
                    overflow: "hidden",
                    background: auditDashboardTheme.colors.surface,
                    margin: 0,
                    userSelect: "text",
                  }}
                >
                  <Box
                    sx={{
                      px: 0,
                      py: 0,
                      minHeight: 0,
                      userSelect: "text",
                    }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          gap: "12px",
                          alignItems: "center",
                          px: 2,
                          py: "10px",
                          borderBottom: `1px solid ${auditDashboardTheme.colors.lineSoft}`,
                          bgcolor: auditToneStyles[shiftTone].bg,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            gap: "8px",
                            minWidth: 0,
                            overflow: "hidden",
                          }}
                        >
                          {locationLabel ? (
                            <Box component="span" sx={getLocationChipSx(shift.locationType)}>
                              <LocationOnOutlinedIcon sx={{ mr: "4px", fontSize: 14, color: "inherit" }} />
                              {locationLabel}
                            </Box>
                          ) : null}
                          <Typography
                            component="span"
                            sx={{
                              color: auditDashboardTheme.colors.text,
                              fontSize: 16,
                              fontWeight: 700,
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {`${displayLabel} :`}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              color: auditDashboardTheme.colors.text,
                              fontSize: 15,
                              fontWeight: 700,
                              lineHeight: 1.2,
                              whiteSpace: { xs: "normal", sm: "nowrap" },
                            }}
                          >
                            {formatShiftRangeLabel(shift, shiftReportDate)}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              color: auditDashboardTheme.colors.textMutedStrong,
                              fontSize: 14,
                              fontWeight: 700,
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getShiftOperatorLabel(shift)}
                          </Typography>
                          {shift.isAutogenerated ? (
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 18,
                                px: 0.85,
                                border: "1px solid #e5e9f2",
                                borderRadius: 999,
                                bgcolor: "#f6f7fb",
                                color: "#6b7280",
                                fontSize: 10,
                                fontWeight: 700,
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Auto
                            </Box>
                          ) : null}
                        </Box>
                        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", minWidth: 0 }}>
                          {!disableShiftExpand ? (
                            <Tooltip title={isShiftExpanded ? "Close shift report" : "Open shift report"}>
                              <IconButton
                                size="small"
                                aria-label={isShiftExpanded ? "Close shift report" : "Open shift report"}
                                aria-expanded={isShiftExpanded}
                                aria-controls={shiftReportPanelId}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleShiftExpanded(shiftKey);
                                }}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  p: 0,
                                  color: auditDashboardTheme.colors.blue,
                                  "& .shift-card-chevron": {
                                    transition: "transform 160ms ease",
                                    transform: isShiftExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                  },
                                }}
                              >
                                <ChevronRightIcon className="shift-card-chevron" sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </Box>

                      <ShiftSummaryTable shift={shift} hasPosType={hasPosType} />
                    </Box>
                  </Box>

                  {!disableShiftExpand ? (
                    <Collapse in={isShiftExpanded} timeout="auto" unmountOnExit>
                      <Box
                        id={shiftReportPanelId}
                        sx={{
                          px: 0,
                          pt: 0,
                          pb: 0,
                          borderTop: 0,
                          overflow: "hidden",
                          background: auditDashboardTheme.colors.surface,
                        }}
                      >
                        <Box sx={{ height: 1, mx: { xs: 1.75, sm: 3 }, bgcolor: auditDashboardTheme.colors.line }} />
                        <Box sx={{ px: "8px", pb: "8px" }}>
                          <ShiftScratcherDetails
                            storeId={storeId}
                            auditId={shift.auditId}
                            reportDate={shiftReportDate}
                            isExpanded={isShiftExpanded}
                            hasPosType={hasPosType}
                            ignorePosData={shift.ignorePosData}
                            defaultTab={isShiftMismatched ? "mismatched" : "all"}
                          />
                        </Box>
                      </Box>
                    </Collapse>
                  ) : null}
                </Box>
              );
                })}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
